import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicSubscription } from "../../subscriptions/hooks/useClinicSubscription"
import { apiFetch } from "../../../utils/apiFetch"
import PlanWorkspaceHeader from "../components/PlanWorkspaceHeader"
import PlanMetricStrip from "../components/PlanMetricStrip"
import PlanItemsSection from "../components/PlanItemsSection"
import { SectionCard } from "../components/PlanPrimitives"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function formatDateTime(value) {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  })
}

function buildPrintableHtml({
  plan,
  patientId,
  totalEstimated,
  totalPaid,
  pendingFinancial,
  clinicName,
  createdByName
}) {
  const patientName = plan?.patient
    ? `${plan.patient.firstName || ""} ${plan.patient.lastName || ""}`.trim()
    : patientId || "Paciente"

  const rows = (plan?.items || [])
    .map((item, index) => {
      const treatmentName = item.treatment?.name || item.treatmentName || "Tratamiento"
      const tooth = item.tooth ?? "-"
      const surface = item.surface || "-"
      const condition = item.condition || "-"
      const price = formatMoney(item.price || 0)
      const status = item.status || "-"
      const notes = item.notes || "-"

      return `
        <tr>
          <td>${index + 1}</td>
          <td>${treatmentName}</td>
          <td>${tooth}</td>
          <td>${surface}</td>
          <td>${condition}</td>
          <td>${price}</td>
          <td>${status}</td>
          <td>${notes}</td>
        </tr>
      `
    })
    .join("")

  return `
    <html>
      <head>
        <title>Plan de tratamiento</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            color: #0f172a;
            margin: 32px;
          }
          h1 {
            font-size: 28px;
            margin: 0 0 8px;
          }
          .muted {
            color: #64748b;
            font-size: 13px;
            margin-bottom: 18px;
          }
          .section {
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 16px;
            margin-bottom: 16px;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          .label {
            font-size: 11px;
            text-transform: uppercase;
            color: #64748b;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .value {
            font-size: 14px;
            font-weight: 700;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 12px;
          }
          th, td {
            border: 1px solid #e2e8f0;
            padding: 8px;
            text-align: left;
            vertical-align: top;
            font-size: 12px;
          }
          th {
            background: #f8fafc;
            font-size: 11px;
            text-transform: uppercase;
          }
          .totals {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            margin-top: 12px;
          }
          .metric {
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 12px;
            background: #ffffff;
          }
          .metric .label {
            margin-bottom: 6px;
          }
          .footer {
            margin-top: 24px;
            font-size: 12px;
            color: #64748b;
          }
        </style>
      </head>
      <body>
        <h1>${plan?.title || "Plan de tratamiento"}</h1>
        <div class="muted">Documento imprimible para entrega al paciente</div>

        <div class="section">
          <div class="grid">
            <div>
              <div class="label">Paciente</div>
              <div class="value">${patientName}</div>
            </div>
            <div>
              <div class="label">ClÃ­nica</div>
              <div class="value">${clinicName || "-"}</div>
            </div>
            <div>
              <div class="label">Fecha de creaciÃ³n</div>
              <div class="value">${formatDateTime(plan?.createdAt)}</div>
            </div>
            <div>
              <div class="label">Estado del plan</div>
              <div class="value">${plan?.status || "-"}</div>
            </div>
            <div>
              <div class="label">Creado por</div>
              <div class="value">${createdByName || "-"}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="label">Notas generales</div>
          <div class="value">${plan?.notes || "-"}</div>
        </div>

        <div class="section">
          <div class="label">Tratamientos incluidos</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Tratamiento</th>
                <th>Pieza</th>
                <th>Superficie</th>
                <th>CondiciÃ³n</th>
                <th>Precio</th>
                <th>Estatus</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              ${rows || `<tr><td colspan="8">Sin Ã­tems capturados.</td></tr>`}
            </tbody>
          </table>

          <div class="totals">
            <div class="metric">
              <div class="label">Estimado</div>
              <div class="value">${formatMoney(totalEstimated)}</div>
            </div>
            <div class="metric">
              <div class="label">Pagado</div>
              <div class="value">${formatMoney(totalPaid)}</div>
            </div>
            <div class="metric">
              <div class="label">Saldo pendiente</div>
              <div class="value">${formatMoney(pendingFinancial)}</div>
            </div>
          </div>
        </div>

        <div class="footer">
          Este documento corresponde al plan de tratamiento registrado en OdontoFlow.
        </div>
      </body>
    </html>
  `
}

export default function TreatmentPlanWorkspacePage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { activeClinicId } = useActiveClinic()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const storedPlanContext = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("odontoflow_treatment_plan_context") || "{}")
    } catch {
      return {}
    }
  }, [])

  const patientIdFromUrl = queryParams.get("patientId") || storedPlanContext.patientId || ""
  const clinicIdFromUrl = queryParams.get("clinicId") || storedPlanContext.clinicId || ""

  const clinicId =
    clinicIdFromUrl ||
    (activeClinicId && activeClinicId !== "ALL" ? activeClinicId : "") ||
    (localStorage.getItem("odontoflow_clinic_id") !== "ALL"
      ? localStorage.getItem("odontoflow_clinic_id") || ""
      : "")

  const { subscription } = useClinicSubscription(clinicId)
  const isReadOnly = subscription?.access?.isReadOnly || false
  const readOnlyReason =
    subscription?.access?.reason || "La clÃ­nica estÃ¡ en modo solo lectura."

  const [loading, setLoading] = useState(false)
  const [savingItemId, setSavingItemId] = useState("")
  const [producingItemId, setProducingItemId] = useState("")
  const [error, setError] = useState("")
  const [plan, setPlan] = useState(null)
  const [editRows, setEditRows] = useState({})

  const loadPlan = useCallback(async () => {
    try {
      if (!id) {
        setPlan(null)
        setError("No se recibiÃ³ id de plan.")
        return
      }

      setLoading(true)
      setError("")

      const res = await apiFetch(`${API_BASE}/appointments/treatment-plan/${id}`)
      const data = await res.json().catch(() => null)

      if (!res.ok || !data) {
        setPlan(null)
        setError(data?.message || "No se pudo cargar el plan.")
        return
      }

      setPlan(data)

      const initialEditRows = {}
      ;(data.items || []).forEach((item) => {
        initialEditRows[item.id] = {
          assignedDoctorId: item.assignedDoctorId || "",
          suggestedDoctorId: item.suggestedDoctorId || "",
          price: String(item.price ?? ""),
          notes: item.notes || ""
        }
      })

      setEditRows(initialEditRows)
    } catch {
      setPlan(null)
      setError("Error al cargar el plan.")
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadPlan()
  }, [loadPlan])

  const items = useMemo(() => plan?.items || [], [plan])

  const doctorOptions = useMemo(() => {
    const map = new Map()

    items.forEach((item) => {
      if (item.assignedDoctor?.id) {
        map.set(item.assignedDoctor.id, item.assignedDoctor.name || "Doctor")
      }
      if (item.suggestedDoctor?.id) {
        map.set(item.suggestedDoctor.id, item.suggestedDoctor.name || "Doctor")
      }
      if (item.appointment?.doctor?.id) {
        map.set(item.appointment.doctor.id, item.appointment.doctor.name || "Doctor")
      }
    })

    return Array.from(map.entries()).map(([doctorId, name]) => ({
      id: doctorId,
      name
    }))
  }, [items])

  const totalPaid = Number(plan?.totalPaid || 0)
  const totalEstimated = Number(plan?.totalEstimated || 0)
  const totalProduced = Number(plan?.totalProduced || 0)
  const pendingFinancial = Math.max(0, totalEstimated - totalPaid)

  const counts = useMemo(() => {
    const result = {
      pending: 0,
      scheduled: 0,
      inProgress: 0,
      done: 0,
      cancelled: 0
    }

    items.forEach((item) => {
      const s = String(item.status || "").toUpperCase()
      if (s === "PENDING") result.pending += 1
      if (s === "SCHEDULED") result.scheduled += 1
      if (s === "IN_PROGRESS") result.inProgress += 1
      if (s === "DONE") result.done += 1
      if (s === "CANCELLED") result.cancelled += 1
    })

    return result
  }, [items])

  const patientId = plan?.patientId || patientIdFromUrl || plan?.patient?.id || ""
  const effectiveClinicId = plan?.clinicId || clinicId || ""

  const clinicName =
    plan?.clinic?.name ||
    plan?.clinicName ||
    plan?.clinic?.title ||
    localStorage.getItem("odontoflow_clinic_name") ||
    "-"

  const createdByName =
    plan?.createdBy?.name ||
    plan?.createdByName ||
    plan?.createdByUser?.name ||
    plan?.createdBy?.fullName ||
    plan?.user?.name ||
    "-"

  const updateEditField = useCallback((itemId, field, value) => {
    setEditRows((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }))
  }, [])

  const saveItemChanges = useCallback(
    async (item) => {
      try {
        if (isReadOnly) {
          alert(readOnlyReason)
          return
        }

        setSavingItemId(item.id)

        const row = editRows[item.id] || {}

        const res = await apiFetch(`${API_BASE}/appointments/treatment-plan-item`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            itemId: item.id,
            assignedDoctorId: row.assignedDoctorId || null,
            suggestedDoctorId: row.suggestedDoctorId || null,
            price: row.price === "" ? undefined : Number(row.price),
            notes: row.notes || null
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo guardar el Ã­tem.")
          return
        }

        await loadPlan()
        alert("Ãtem actualizado correctamente.")
      } catch {
        alert("Error al guardar cambios.")
      } finally {
        setSavingItemId("")
      }
    },
    [editRows, isReadOnly, readOnlyReason, loadPlan]
  )

  const updateItemStatus = useCallback(
    async (item, status) => {
      try {
        if (isReadOnly) {
          alert(readOnlyReason)
          return
        }

        setSavingItemId(item.id)

        const res = await apiFetch(`${API_BASE}/appointments/treatment-plan-item`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            itemId: item.id,
            status
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo actualizar estatus.")
          return
        }

        await loadPlan()
        alert("Estatus actualizado.")
      } catch {
        alert("Error al actualizar estatus.")
      } finally {
        setSavingItemId("")
      }
    },
    [isReadOnly, readOnlyReason, loadPlan]
  )

  const scheduleItem = useCallback(
    (item) => {
      if (isReadOnly) {
        alert(readOnlyReason)
        return
      }

      if (!patientId || !effectiveClinicId) {
        alert("Faltan datos de paciente o clÃ­nica.")
        return
      }

      localStorage.setItem(
        "odontoflow_new_appointment_context",
        JSON.stringify({
          patientId,
          clinicId: effectiveClinicId,
          treatmentId: item.treatmentId || "",
          treatmentPlanItemId: item.id || "",
          treatmentPlanId: plan?.id || "",
          planTitle: plan?.title || "",
          source: "treatment_plan_detail"
        })
      )

      navigate("/agenda")
    },
    [isReadOnly, readOnlyReason, patientId, effectiveClinicId, plan, navigate]
  )

  const openFinance = useCallback(
    (focus = "RECEIVABLES") => {
      if (!patientId || !effectiveClinicId) {
        alert("Faltan datos de paciente o clÃ­nica.")
        return
      }

      const params = new URLSearchParams({
        clinicId: String(effectiveClinicId),
        tab: "FINANCE",
        focus: String(focus).toUpperCase()
      })

      navigate(`/patients/${patientId}?${params.toString()}`)
    },
    [patientId, effectiveClinicId, navigate]
  )

  const openPatient = useCallback(() => {
    if (!patientId || !effectiveClinicId) {
      alert("Faltan datos de paciente o clÃ­nica.")
      return
    }

    navigate(`/patients/${patientId}?clinicId=${effectiveClinicId}&tab=PLANS`)
  }, [patientId, effectiveClinicId, navigate])

  const openAppointment = useCallback(
    ({ appointmentId, appointmentStart }) => {
      if (!appointmentId) {
        alert("Este Ã­tem no tiene cita ligada.")
        return
      }

      localStorage.setItem(
        "odontoflow_open_appointment",
        JSON.stringify({
          appointmentId,
          clinicId: effectiveClinicId,
          date: appointmentStart
            ? new Date(appointmentStart).toISOString().slice(0, 10)
            : null
        })
      )

      navigate("/agenda")
    },
    [effectiveClinicId, navigate]
  )

  const registerProducedTreatment = useCallback(
    async (item) => {
      try {
        if (isReadOnly) {
          alert(readOnlyReason)
          return
        }

        if (!item.appointmentId) {
          alert("Primero agenda este tratamiento para poder registrarlo.")
          return
        }

        const doctorId =
          item.assignedDoctorId || item.appointmentDoctorId || item.suggestedDoctorId

        if (!doctorId) {
          alert("Este Ã­tem no tiene doctor asignado.")
          return
        }

        if (!patientId || !effectiveClinicId) {
          alert("Faltan datos de paciente o clÃ­nica.")
          return
        }

        setProducingItemId(item.id)

        const res = await apiFetch(`${API_BASE}/appointments/register-treatment-from-appointment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            clinicId: effectiveClinicId,
            appointmentId: item.appointmentId,
            patientId,
            doctorId,
            treatmentId: item.treatmentId,
            tooth: item.tooth || undefined,
            surface: item.surface || undefined,
            condition: item.condition || undefined,
            status: "COMPLETED"
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo registrar el tratamiento.")
          return
        }

        await loadPlan()
        alert("Tratamiento registrado correctamente.")
      } catch {
        alert("Error al registrar el tratamiento.")
      } finally {
        setProducingItemId("")
      }
    },
    [isReadOnly, readOnlyReason, patientId, effectiveClinicId, loadPlan]
  )

  const printPlan = useCallback(() => {
    if (!plan) {
      alert("AÃºn no hay plan cargado para imprimir.")
      return
    }

    const printWindow = window.open("", "_blank", "width=980,height=720")
    if (!printWindow) {
      alert("No se pudo abrir la ventana de impresiÃ³n.")
      return
    }

    printWindow.document.open()
    printWindow.document.write(
      buildPrintableHtml({
        plan,
        patientId,
        totalEstimated,
        totalPaid,
        pendingFinancial,
        clinicName,
        createdByName
      })
    )
    printWindow.document.close()
    printWindow.focus()

    setTimeout(() => {
      printWindow.print()
    }, 300)
  }, [
    plan,
    patientId,
    totalEstimated,
    totalPaid,
    pendingFinancial,
    clinicName,
    createdByName
  ])

  return (
    <div
      style={{
        padding: 24,
        background: "#f8fafc",
        minHeight: "100vh",
        display: "grid",
        gap: 16
      }}
    >
      <PlanWorkspaceHeader
        plan={plan}
        patientId={patientId}
        effectiveClinicId={effectiveClinicId}
        formatDateTime={formatDateTime}
        onOpenPatient={openPatient}
        onOpenFinance={openFinance}
        onPrint={printPlan}
        onGoBack={() => navigate("/treatment-plans")}
      />

      {isReadOnly ? (
        <div
          style={{
            padding: 12,
            borderRadius: 14,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontWeight: 800,
            boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
          }}
        >
          ðŸ”’ Modo solo lectura â€” {readOnlyReason}
        </div>
      ) : null}

      {loading ? (
        <SectionCard title="Cargando plan">
          <div style={{ color: "#64748b" }}>Estamos preparando la informaciÃ³n del plan...</div>
        </SectionCard>
      ) : error ? (
        <SectionCard title="No se pudo cargar el plan">
          <div
            style={{
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              borderRadius: 14,
              padding: 16,
              fontWeight: 700
            }}
          >
            {error}
          </div>
        </SectionCard>
      ) : !plan ? (
        <SectionCard title="Sin informaciÃ³n del plan">
          <div style={{ color: "#64748b" }}>
            No encontramos informaciÃ³n disponible para este plan.
          </div>
        </SectionCard>
      ) : (
        <>
          <PlanMetricStrip
            formatMoney={formatMoney}
            totalEstimated={totalEstimated}
            totalProduced={totalProduced}
            totalPaid={totalPaid}
            pendingFinancial={pendingFinancial}
            items={items}
            counts={counts}
          />

          <PlanItemsSection
            items={items}
            editRows={editRows}
            doctorOptions={doctorOptions}
            isReadOnly={isReadOnly}
            savingItemId={savingItemId}
            producingItemId={producingItemId}
            updateEditField={updateEditField}
            saveItemChanges={saveItemChanges}
            scheduleItem={scheduleItem}
            openAppointment={openAppointment}
            registerProducedTreatment={registerProducedTreatment}
            updateItemStatus={updateItemStatus}
            formatMoney={formatMoney}
            formatDateTime={formatDateTime}
          />
        </>
      )}
    </div>
  )
}
